import React, { useCallback, useEffect, useState } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Shield as ShieldIcon,
  ShieldOutlined as ShieldOutlinedIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import { toServerDate } from '../../timezone';

const fmtDate = (iso) => (toServerDate(iso)?.toLocaleString() ?? '—');

/**
 * Shows ClickHouse Row Level Security provisioning status for all tenants
 * and allows provisioning / backfilling from the operator UI.
 */
const ClickHouseRlsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [provisioningId, setProvisioningId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [rowMsg, setRowMsg] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await billingService.getRlsStatus();
      setRows(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load RLS status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleProvision = async (orgId, orgSlug) => {
    setProvisioningId(orgId);
    setRowMsg((m) => ({ ...m, [orgId]: null }));
    try {
      await billingService.provisionTenantRls(orgId);
      setRowMsg((m) => ({ ...m, [orgId]: { type: 'success', text: 'Provisioned' } }));
      await load();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Provisioning failed';
      setRowMsg((m) => ({ ...m, [orgId]: { type: 'error', text: msg } }));
    } finally {
      setProvisioningId(null);
    }
  };

  const handleBackfillAll = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await billingService.provisionAllRls();
      setBackfillResult(res.data);
      await load();
    } catch (e) {
      setBackfillResult({ error: e?.response?.data?.error || e.message || 'Backfill failed' });
    } finally {
      setBackfilling(false);
    }
  };

  const provisionedCount = rows.filter((r) => r.rls_provisioned).length;
  const totalAssigned = rows.filter((r) => r.cluster_id != null).length;
  const needsProvision = rows.filter((r) => r.cluster_id != null && !r.rls_provisioned).length;

  return (
    <Paper sx={{ p: 2, mt: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ShieldIcon color="primary" fontSize="small" />
            <Typography variant="h6">Row Level Security</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Per-tenant ClickHouse users with RESTRICTIVE row policies — database-engine isolation
            independent of application logic. Provisioned tenants connect with their own credentials
            and can only see their own <code>org_id</code> rows.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0} ml={2}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || backfilling}>
            Refresh
          </Button>
          <Tooltip title={needsProvision === 0 ? 'All assigned tenants already provisioned' : `Provision ${needsProvision} remaining tenant(s)`}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={backfilling ? <CircularProgress size={14} color="inherit" /> : <ShieldIcon />}
                onClick={handleBackfillAll}
                disabled={backfilling || loading || needsProvision === 0}
              >
                Backfill All
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Summary chips */}
      <Stack direction="row" spacing={1} mb={2}>
        <Chip
          icon={<CheckIcon />}
          label={`${provisionedCount} provisioned`}
          size="small"
          color={provisionedCount > 0 ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          icon={<ShieldOutlinedIcon />}
          label={`${needsProvision} pending`}
          size="small"
          color={needsProvision > 0 ? 'warning' : 'default'}
          variant="outlined"
        />
        <Chip
          label={`${rows.length - totalAssigned} unassigned (no cluster)`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {backfillResult && (
        <Alert
          severity={backfillResult.error ? 'error' : (backfillResult.summary?.failed_count > 0 ? 'warning' : 'success')}
          onClose={() => setBackfillResult(null)}
          sx={{ mb: 2 }}
        >
          {backfillResult.error ? (
            backfillResult.error
          ) : (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Backfill complete — provisioned: {backfillResult.summary?.provisioned_count ?? 0},
                skipped: {backfillResult.summary?.skipped_count ?? 0},
                failed: {backfillResult.summary?.failed_count ?? 0}
              </Typography>
              {backfillResult.failed?.length > 0 && (
                <Box mt={0.5}>
                  {backfillResult.failed.map((f) => (
                    <Typography key={f.org_id} variant="caption" display="block" color="error.main">
                      Org {f.org_id}: {f.error}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cluster</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>RLS Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>CH Username</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Provisioned At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.org_id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{row.org_slug}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {row.cluster_id ? (
                    <Chip label={`Cluster ${row.cluster_id}`} size="small" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.disabled" fontStyle="italic">No cluster</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {row.rls_provisioned ? (
                    <Chip icon={<CheckIcon />} label="Provisioned" size="small" color="success" />
                  ) : row.cluster_id ? (
                    <Chip icon={<ErrorIcon />} label="Not provisioned" size="small" color="warning" />
                  ) : (
                    <Chip label="No cluster" size="small" color="default" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {row.ch_username || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                    {fmtDate(row.provisioned_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {row.cluster_id && (
                      <Button
                        size="small"
                        variant="outlined"
                        color={row.rls_provisioned ? 'success' : 'primary'}
                        disabled={provisioningId === row.org_id || backfilling}
                        startIcon={provisioningId === row.org_id
                          ? <CircularProgress size={12} />
                          : row.rls_provisioned ? <CheckIcon /> : <ShieldIcon />}
                        onClick={() => handleProvision(row.org_id, row.org_slug)}
                      >
                        {row.rls_provisioned ? 'Re-provision' : 'Provision'}
                      </Button>
                    )}
                    {rowMsg[row.org_id] && (
                      <Typography
                        variant="caption"
                        color={rowMsg[row.org_id].type === 'success' ? 'success.main' : 'error.main'}
                      >
                        {rowMsg[row.org_id].text}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No organizations found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default ClickHouseRlsPanel;
